import {action, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IAuthApiProvider, ILoginPayload} from "data/providers/api/auth.api.provider";
import type {
	IAPILogin,
	IAPIRegister,
	IPgaAppLogin,
	IPgaAppRegister,
	IRegistrationPayload,
	IUpdateUserPayload,
	IUserApiProvider,
} from "data/providers/api/user.api.provider";
import type {
	IForgotPasswordPayload,
	IPasswordApiProvider,
	IResetPasswordPayload,
} from "data/providers/api/password.api.provider";
import type {AxiosResponse} from "axios";
import {TrackAnalytics} from "data/utils/analytic_service";

export interface IUser {
	id: number;
	email: string;
	displayName: string;
	firstName: string | null;
	lastName: string | null;
	country: string | null;
	isNotificationsEnabled: boolean;
	createdAt: string;
}

export interface IUserGigyaProfile {
	firstName: string;
	lastName: string;
	birthDay: number;
	birthYear: number;
	birthMonth: number;
	country: string;
}

export interface IUserStore {
	get user(): IUser | undefined;
	get isAuthorized(): boolean;
	get wasLoggedOut(): boolean;
	get userLoginParams(): IAPILogin;
	get userProfile(): IUserGigyaProfile;

	clearUser: () => void;

	forgotPassword(payload: IForgotPasswordPayload): Promise<AxiosResponse<void>>;
	resetPassword(payload: IResetPasswordPayload): Promise<AxiosResponse<void>>;
	register(payload: IRegistrationPayload): Promise<void>;
	update(payload: IUpdateUserPayload): Promise<void>;
	deactivate(): Promise<void>;
	login(payload: ILoginPayload): Promise<void>;
	logout(): Promise<void>;
	requestUser(): Promise<void>;
	backdoor(payload: ILoginPayload): Promise<void>;
	flushUser(): void;
	ssoWebLogin(payload: IAPILogin): Promise<void>;
	ssoAppLogin(payload: IPgaAppLogin): Promise<void>;
	ssoWebRegister(payload: IAPIRegister): Promise<void>;
	ssoAppRegister(payload: IPgaAppRegister): Promise<void>;
	ssoLogout(): Promise<void>;

	setUserParams: (payload: IAPILogin) => void;
	setUserProfile: (payload: IUserGigyaProfile) => void;
	welcomeBackEmail: (payload: string) => Promise<void>;
}

@injectable()
export class UserStore implements IUserStore {
	@observable private _user?: IUser = undefined;
	@observable private _wasLoggedOut = false;
	@observable private _loginParams: IAPILogin = {
		uid: "",
		uidSignature: "",
		signatureTimestamp: "",
	};
	@observable private _userProfile: IUserGigyaProfile = {
		firstName: "",
		lastName: "",
		birthDay: 1,
		birthYear: 1990,
		birthMonth: 1,
		country: "",
	};

	constructor(
		@inject(Bindings.AuthApiProvider) private _authApi: IAuthApiProvider,
		@inject(Bindings.UserApiProvider) private _userApi: IUserApiProvider,
		@inject(Bindings.PasswordApiProvider) private _passwordApi: IPasswordApiProvider
	) {
		makeAutoObservable(this);
	}

	get isAuthorized() {
		return Boolean(this.user);
	}

	get wasLoggedOut() {
		return this._wasLoggedOut;
	}

	get user() {
		return this._user;
	}

	get userLoginParams() {
		return this._loginParams;
	}

	get userProfile() {
		return this._userProfile;
	}

	@action clearUser = () => {
		this._user = undefined;
	};

	@action setUserParams = (payload: IAPILogin) => {
		this._loginParams = payload;
	};

	@action setUserProfile = (payload: IUserGigyaProfile) => {
		this._userProfile = payload;
	};

	@action
	async requestUser() {
		const response = await this._userApi.user();
		const {user} = response.data.success;

		runInAction(() => {
			window.ats?.setAdditionalData({
				type: "email",
				id: user.email,
			});

			this._user = user;
		});
	}

	@action
	async login(payload: ILoginPayload) {
		const response = await this._authApi.login(payload);
		const {user} = response.data.success;

		runInAction(() => {
			window.ats?.setAdditionalData({
				type: "email",
				id: user.email,
			});

			this._user = user;
			this._wasLoggedOut = false;
		});
	}

	@action
	async ssoWebLogin(payload: IAPILogin) {
		const response = await this._authApi.gigyaLogin(payload);
		const {user} = response.data.success;

		runInAction(() => {
			window.ats?.setAdditionalData({
				type: "email",
				id: user.email,
			});

			this._user = user;
			this._wasLoggedOut = false;
		});
	}

	@action
	async ssoAppLogin(payload: IPgaAppLogin) {
		const response = await this._authApi.pgaAppLogin(payload);
		const {user} = response.data.success;

		runInAction(() => {
			window.ats?.setAdditionalData({
				type: "email",
				id: user.email,
			});

			this._user = user;
			this._wasLoggedOut = false;
		});
	}

	@action
	async ssoWebRegister(payload: IAPIRegister) {
		const response = await this._authApi.gigyaRegister(payload);
		const {user} = response.data.success;

		runInAction(() => {
			window.ats?.setAdditionalData({
				type: "email",
				id: user.email,
			});

			this._user = user;
			this._wasLoggedOut = false;
		});
	}

	@action
	async ssoAppRegister(payload: IPgaAppRegister) {
		const response = await this._authApi.pgaAppRegister(payload);
		const {user} = response.data.success;

		runInAction(() => {
			window.ats?.setAdditionalData({
				type: "email",
				id: user.email,
			});

			this._user = user;
			this._wasLoggedOut = false;
		});
	}

	@action
	async register(payload: IRegistrationPayload) {
		const response = await this._userApi.register(payload);
		const {user} = response.data.success;

		runInAction(() => {
			window.ats?.setAdditionalData({
				type: "email",
				id: user.email,
			});

			this._user = user;
			this._wasLoggedOut = false;
		});
	}

	@action
	async update(payload: IUpdateUserPayload) {
		const response = await this._userApi.update(payload);
		const {user} = response.data.success;

		runInAction(() => {
			this._user = user;
		});
	}

	@action
	async logout() {
		await this._authApi.logout();

		runInAction(() => {
			TrackAnalytics.trackOnAction({
				event: "Logged Out",
			});

			this._user = undefined;
			this._wasLoggedOut = true;
		});
	}

	@action
	async ssoLogout() {
		await this._authApi.gigyaLogout();

		runInAction(() => {
			TrackAnalytics.trackOnAction({
				event: "Logged Out",
			});

			this._user = undefined;
			this._wasLoggedOut = true;
		});
	}

	@action
	async deactivate() {
		await this._userApi.deactivateAccount();

		runInAction(() => {
			this._user = undefined;
			this._wasLoggedOut = true;
		});
	}

	@action async backdoor(payload: ILoginPayload) {
		const response = await this._authApi.backdoor(payload);
		const {user} = response.data.success;

		runInAction(() => {
			this._user = user;
			this._wasLoggedOut = false;
		});
	}

	@action flushUser() {
		runInAction(() => {
			this._user = undefined;
			this._wasLoggedOut = true;
		});
	}

	forgotPassword(payload: IForgotPasswordPayload) {
		return this._passwordApi.forgotPassword(payload);
	}

	resetPassword(payload: IResetPasswordPayload) {
		return this._passwordApi.resetPassword(payload);
	}

	welcomeBackEmail = async (payload: string) => {
		await this._userApi.welcomeBackEmail(payload);
	};
}
